// components/MarkdownRenderer.tsx
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { Box, Text, Code, Heading, Flex, Separator } from "@chakra-ui/react";
import "highlight.js/styles/github.css"; // 코드 하이라이팅 스타일

interface MarkdownRendererProps {
  content: string;
  fontSize?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  fontSize = "sm",
}) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHighlight]}
      components={{
        // 헤딩
        h1: ({ children }) => (
          <Heading as="h1" size="lg" mb={3} mt={4} color="gray.800">
            {children}
          </Heading>
        ),
        h2: ({ children }) => (
          <Heading as="h2" size="md" mb={3} mt={3} color="gray.800">
            {children}
          </Heading>
        ),
        h3: ({ children }) => (
          <Heading as="h3" size="sm" mb={2} mt={3} color="gray.700">
            {children}
          </Heading>
        ),
        h4: ({ children }) => (
          <Heading as="h4" size="xs" mb={2} mt={2} color="gray.700">
            {children}
          </Heading>
        ),

        // 문단
        p: ({ children }) => (
          <Text fontSize={fontSize} mb={2} lineHeight="1.6" color="gray.800">
            {children}
          </Text>
        ),

        // 강조
        strong: ({ children }) => (
          <Text as="strong" fontWeight="bold" color="gray.900">
            {children}
          </Text>
        ),

        em: ({ children }) => (
          <Text as="em" fontStyle="italic" color="gray.700">
            {children}
          </Text>
        ),

        // 인라인 코드
        code: ({ children, className }) => {
          // className이 없으면 인라인 코드, 있으면 코드 블록
          if (!className) {
            return (
              <Code
                px={1}
                py={0.5}
                fontSize="sm"
                bg="gray.100"
                color="red.600"
                borderRadius="md"
              >
                {children}
              </Code>
            );
          }

          // 코드 블록
          return (
            <Box
              as="pre"
              bg="gray.50"
              p={3}
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
              overflow="auto"
              mb={3}
              mt={2}
            >
              <Code
                display="block"
                whiteSpace="pre"
                fontSize="sm"
                bg="transparent"
                p={0}
                className={className}
              >
                {children}
              </Code>
            </Box>
          );
        },

        // 목록 (HTML 리스트 사용)
        ul: ({ children }) => (
          <Box
            as="ul"
            mb={3}
            pl={4}
            css={{
              listStyleType: "disc",
              "& li": {
                marginBottom: "4px",
                lineHeight: "1.5",
              },
            }}
          >
            {children}
          </Box>
        ),

        ol: ({ children }) => (
          <Box
            as="ol"
            mb={3}
            pl={4}
            css={{
              listStyleType: "decimal",
              "& li": {
                marginBottom: "4px",
                lineHeight: "1.5",
              },
            }}
          >
            {children}
          </Box>
        ),

        li: ({ children }) => (
          <Box as="li" fontSize={fontSize}>
            {children}
          </Box>
        ),

        // 표 (Grid 기반 대체)
        table: ({ children }) => (
          <Box overflowX="auto" mb={3}>
            <Box
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              overflow="hidden"
            >
              {children}
            </Box>
          </Box>
        ),

        thead: ({ children }) => <Box bg="gray.50">{children}</Box>,

        tbody: ({ children }) => <Box>{children}</Box>,

        tr: ({ children }) => (
          <Flex
            borderBottom="1px solid"
            borderColor="gray.200"
            _last={{ borderBottom: "none" }}
          >
            {children}
          </Flex>
        ),

        th: ({ children }) => (
          <Box
            flex="1"
            px={3}
            py={2}
            fontSize="xs"
            fontWeight="bold"
            color="gray.700"
            borderRight="1px solid"
            borderColor="gray.200"
            _last={{ borderRight: "none" }}
          >
            {children}
          </Box>
        ),

        td: ({ children }) => (
          <Box
            flex="1"
            px={3}
            py={2}
            fontSize={fontSize}
            borderRight="1px solid"
            borderColor="gray.200"
            _last={{ borderRight: "none" }}
          >
            {children}
          </Box>
        ),

        // 수평선
        hr: () => <Separator my={4} />,

        // 인용구
        blockquote: ({ children }) => (
          <Box
            borderLeft="4px solid"
            borderColor="blue.400"
            pl={4}
            py={2}
            bg="blue.50"
            mb={3}
            fontStyle="italic"
          >
            {children}
          </Box>
        ),

        // 링크
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#3182ce",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#2c5282";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "#3182ce";
            }}
          >
            {children}
          </a>
        ),

        // 체크박스 (GitHub Flavored Markdown)
        input: ({ type, checked, disabled }) => {
          if (type === "checkbox") {
            return (
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                style={{ marginRight: "8px" }}
                readOnly
              />
            );
          }
          return null;
        },

        // 이미지
        img: ({ src, alt }) => (
          <Box mb={3}>
            <img
              src={src}
              alt={alt}
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
              }}
            />
          </Box>
        ),

        // 코드 언어별 뱃지 추가
        pre: ({ children }) => (
          <Box position="relative" mb={3}>
            {children}
          </Box>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
